'use client';

/**
 * Breadcrumbs Component
 * 
 * Provides navigation breadcrumbs with Schema.org structured data for SEO.
 * Automatically generates breadcrumbs based on the current path.
 * 
 * Usage:
 * <Breadcrumbs />
 * 
 * Or with custom items:
 * <Breadcrumbs items={[
 *   { label: 'Home', href: '/' },
 *   { label: 'Features', href: '/features' },
 *   { label: 'Vendor', href: '/features/vendor' }
 * ]} />
 */

import { usePathname } from 'next/navigation';
import Link from 'next/link';
import { generateBreadcrumbSchema, BreadcrumbItem } from '@/lib/seo/schema';
import JsonLd from './JsonLd';

interface BreadcrumbDisplayItem {
  label: string;
  href: string;
}

interface BreadcrumbsProps {
  items?: BreadcrumbDisplayItem[];
  className?: string;
}

// Map paths to readable labels
const pathLabels: Record<string, string> = {
  '': 'Home',
  'features': 'Features',
  'vendor': 'Vendor',
  'retailer': 'Retailer',
  'pricing': 'Pricing',
  'about': 'About',
  'faq': 'FAQ',
  'compliance': 'Compliance',
  'how-it-works': 'How It Works',
  'knowledge-hub': 'Knowledge Hub',
  'support': 'Support',
  'investors': 'Investors',
  'brokers': 'Brokers',
  'canideal-iq': 'CanIDeal IQ',
  'crop': 'Crop ROI Planner',
  'global-domestic': 'Global & Domestic',
  'sell-products': 'Sell Products',
  'source-products': 'Source Products',
  'terminal': 'Terminal',
  'markets-pricing': 'Markets & Pricing',
  'market-briefs': 'Market Briefs',
  'how-we-keep-you-safe': 'How We Keep You Safe',
  'privacy': 'Privacy Policy',
  'terms': 'Terms of Service',
  'cookies': 'Cookie Policy',
  'api-docs': 'API Documentation',
  'careers': 'Careers',
  'press': 'Press & Media',
  'homepage': 'Home',
};

export default function Breadcrumbs({ items, className = '' }: BreadcrumbsProps) {
  const pathname = usePathname();

  // Generate breadcrumb items from pathname if not provided
  const breadcrumbItems = items || generateBreadcrumbsFromPath(pathname);

  // Don't show breadcrumbs on homepage
  if (breadcrumbItems.length <= 1) {
    return null;
  }

  // Convert to schema format (name/url instead of label/href)
  const schemaItems: BreadcrumbItem[] = breadcrumbItems.map(item => ({
    name: item.label,
    url: `https://canideal.com${item.href}`,
  }));

  // Generate Schema.org structured data
  const breadcrumbSchema = generateBreadcrumbSchema(schemaItems);

  return (
    <>
      <JsonLd data={breadcrumbSchema} />
      <nav
        aria-label="Breadcrumb"
        className={`py-4 ${className}`}
      >
        <ol className="flex items-center space-x-2 text-sm">
          {breadcrumbItems.map((item, index) => {
            const isLast = index === breadcrumbItems.length - 1;

            return (
              <li key={item.href} className="flex items-center">
                {index > 0 && (
                  <svg
                    className="w-4 h-4 mx-2 text-text-secondary"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
                {isLast ? (
                  <span
                    className="font-medium text-text-primary"
                    aria-current="page"
                  >
                    {item.label}
                  </span>
                ) : (
                  <Link
                    href={item.href}
                    className="text-text-secondary hover:text-primary transition-colors"
                  >
                    {item.label}
                  </Link>
                )}
              </li>
            );
          })}
        </ol>
      </nav>
    </>
  );
}

/**
 * Generate breadcrumb items from pathname
 */
function generateBreadcrumbsFromPath(pathname: string): BreadcrumbDisplayItem[] {
  // Remove leading/trailing slashes and split
  const segments = pathname.replace(/^\/|\/$/g, '').split('/').filter(Boolean);

  // Always start with home
  const items: BreadcrumbDisplayItem[] = [
    { label: 'Home', href: '/' },
  ];

  // Build breadcrumbs from segments
  let currentPath = '';
  segments.forEach((segment) => {
    currentPath += `/${segment}`;
    const label = pathLabels[segment] || formatSegment(segment);
    items.push({
      label,
      href: currentPath,
    });
  });

  return items;
}

/**
 * Format segment to readable label
 */
function formatSegment(segment: string): string {
  return segment
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

